import ChoroplethViz from '../vizs/ChoroplethViz'
import BaseComponent from './BaseComponent'
/**
 * {-EN-}
 * # {componentLabel} (`class {componentClassName}`)
 * Used to display thematic data on a map, in which areas are colored based on a categorical variable.
 *
 * Choropleth maps provide an easy way to visualize how a measurement varies across a geographic area or show the level of variability within a region.
 *
 *  ### Example Usage
 * ```javascript
 *   import {ChoroplethCat} from 'sdv-lib'
 *
 *   // Instantiate the chart with a DOM element, a data Array and a config Object
 *   let myChart = new ChoroplethCat($el, data, config);
 *
 * ```
 *
 * ### Data structure {.section-data-structure}
 * The tabular data for this chart should be a table with 2 columns.
 *
 * Each row represents a region  in the map.
 *
 * The first column represents the id of the region (geojson feature). See [maps](#/developer/PropDetails#features) for more info on how to get the right ids in each case.
 *
 * The second column represents color categroy of the region.
 *
 * | id           |  category|
 * | --------     | ------ |
 * |   ESP        |    cat1|
 * |   FRA        |    cat1|
 * |   USA        |    cat2|
 * |   GBR        |    cat3|
 *
 * {.data-table}
 *
 * {-ES-}
 * # {componentLabelEs} (`class {componentClassName}`)
 * Se utiliza para mostrar datos temáticos en un mapa, en el que las áreas se colorean según una variable categórica.
 *
 * Los mapas de coropletas proporcionan una manera fácil de visualizar cómo varía una medición en un área geográfica o mostrar el nivel de variabilidad dentro de una región.
 *
 *  ### Ejemplo de uso
 * ```javascript
 *   import {ChoroplethCat} from 'sdv-lib'
 *
 *   // Instantiate the chart with a DOM element, a data Array and a config Object
 *   let myChart = new ChoroplethCat($el, data, config);
 *
 * ```
 *
 * ### Estructura de datos {.section-data-structure}
 * Los datos tabulares de este gráfico deben ser una tabla con 2 columnas.
 *
 * Cada fila representa una región en el mapa.
 *
 * La primera columna representa la identificación de la región (característica geojson). Ver [mapas](#/developer/PropDetails#features) para obtener más información sobre cómo obtener los identificadores correctos en cada caso.
 *
 * La segunda columna representa la categoría de color de la región.
 *
 * | id           |  categoría|
 * | --------     | ------ |
 * |   ESP        |    cat1|
 * |   FRA        |    cat1|
 * |   USA        |    cat2|
 * |   GBR        |    cat3|
 *
 * {.data-table}
 */
export default class ChoroplethCatComponent extends BaseComponent {
  static vizClass = ChoroplethViz
  static componentLabel = 'Choropleth Map - Categorical'
  static componentLabelEs = 'Mapa de coropletas - Categórico'
  static dataFormat = [
    { index: 0, axis: '', label: 'id', types: [String], role: 'feature-id', roleLabel: 'Area' },
    {
      index: 1,
      axis: '',
      label: 'category',
      types: [String, null],
      role: 'value',
      roleLabel: 'Category',
    },
  ]

  static defaultConfig = {
    title: this.componentLabel,
    hasColorPatterns: {
      type: Boolean,
      default: true,
      exposed: false,
    },
    ItemsHelper: {
      colorScale: {
        value: 'categorical',
        options: ['categorical'],
        nonEditable: true,
      },
      colorKey: 'category',
    },
    TooltipBehavior: {
      getHtml: function (d) {
        return `
        <div class="important">${d.label}</div>
        <div>
          <span class="icon" style="display: inline-block; width: 9px; height: 9px; border-radius: 50%; background: ${this.color(
            d
          )}; margin-right: 3px"></span>
          ${d.category}
        </div>
        `
      },
    },
  }
  static dataRequirements = {
    requirementsStrength: 100,
  }
}
